import { useRouter } from 'next/router';

import clsx from 'clsx';
import { useAtomValue, useSetAtom } from 'jotai';

// Hooks
import usePosts from '@/utils/hooks/giveaway-picker/usePosts';
import useTranslation from '@/utils/hooks/translation/useTranslation';

// Libs
import { giveawayPickerUrl } from '@/lib/urls/giveawayPickerUrl';

import Button, { ERROR_SECONDARY } from '@/components/base/Button';
import CompoundBanner from '@/components/base/InfoBanner/CompoundBanner';
// Atoms
import { selectedPostAtom } from '@/components/pages/giveaway-picker/Atoms/postsAtoms';
import { socialProfileAtom } from '@/components/pages/giveaway-picker/Atoms/socialProfileAtom';
import { Pagination } from '@/components/pages/giveaway-picker/SelectPostWrapper/components/Pagination';
import { NoPostsFound } from '@/components/pages/giveaway-picker/SelectPostWrapper/components/SelectPost/NoPostsFound';
// Components
import { Post } from '@/components/pages/giveaway-picker/SelectPostWrapper/components/SelectPost/Post';
import { SelectPostPlaceholder } from '@/components/pages/giveaway-picker/SelectPostWrapper/components/SelectPost/SelectPostPlaceholder';

export const SelectPost = () => {
    const router = useRouter();
    const { t } = useTranslation('giveaway_picker');
    const selectedPost = useAtomValue(selectedPostAtom);
    const setSelectedPost = useSetAtom(selectedPostAtom);
    const selectedProfile = useAtomValue(socialProfileAtom);
    const { error, isLoadingInitialData, data: posts } = usePosts(selectedProfile?.id);

    if (error) {
        const errorMessage =
            error?.response?.data?.message || t('post_selection.error_loading_posts');

        return (
            <CompoundBanner>
                <CompoundBanner.Icon />
                <CompoundBanner.Content message={errorMessage} />
                <Button
                    variant={ERROR_SECONDARY}
                    size="sm"
                    onClick={() => router.push(giveawayPickerUrl)}
                    data-testid="reauth-button">
                    {t('reauth')}
                </Button>
            </CompoundBanner>
        );
    }

    if (isLoadingInitialData) {
        return <SelectPostPlaceholder />;
    }

    if (!posts.length) {
        return <NoPostsFound />;
    }

    return (
        <>
            <div
                className="grid grid-cols-1 gap-4 px-0.75 @sm:grid-cols-1 @md:grid-cols-2 @lg:grid-cols-3"
                data-testid="posts-container">
                {posts.map((post) => (
                    <button
                        key={post.id}
                        onClick={() => {
                            setSelectedPost(post);
                        }}
                        className={clsx(
                            'w-full rounded-lg focus:ring-0 @sm:col-span-1 @md:col-span-1 @lg:col-span-1',
                            {
                                'ring-2 ring-icoBlue ring-offset-0 focus:ring-2 focus:ring-icoBlue focus:ring-offset-0':
                                    selectedPost?.id === post.id
                            }
                        )}
                        data-testid="select-post">
                        <Post
                            post={post}
                            summaryItem={false}
                            selected={selectedPost?.id === post.id}
                        />
                    </button>
                ))}
            </div>
            <Pagination />
        </>
    );
};
